/**
 * localStorage persistence for user-defined Stargrave content.
 * Kept separate from Frostgrave custom content — custom elements are never
 * shared between game systems.
 *
 * Keys:
 *   frostgrave:stargrave:custom-equipment → StargraveEquipment[]
 *   frostgrave:stargrave:custom-soldiers  → StargraveSoldierProfile[]
 *   frostgrave:stargrave:custom-buffs     → StargraveBuff[]
 */

import { StargraveBuff, StargraveEquipment, StargraveSoldierProfile } from '../types';

const KEY_EQUIPMENT = 'frostgrave:stargrave:custom-equipment';
const KEY_SOLDIERS = 'frostgrave:stargrave:custom-soldiers';
const KEY_BUFFS = 'frostgrave:stargrave:custom-buffs';

function readAll<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T[]) : [];
  } catch {
    return [];
  }
}

function writeAll(key: string, items: unknown[]): void {
  localStorage.setItem(key, JSON.stringify(items));
}

export const stargraveCustomContentService = {
  getAllEquipment(): StargraveEquipment[] {
    return readAll<StargraveEquipment>(KEY_EQUIPMENT);
  },

  createEquipment(data: Omit<StargraveEquipment, 'id' | 'isCustom'>): StargraveEquipment {
    const item: StargraveEquipment = { ...data, id: crypto.randomUUID(), isCustom: true };
    writeAll(KEY_EQUIPMENT, [...readAll<StargraveEquipment>(KEY_EQUIPMENT), item]);
    return item;
  },

  updateEquipment(id: string, data: Omit<StargraveEquipment, 'id' | 'isCustom'>): StargraveEquipment | null {
    const all = readAll<StargraveEquipment>(KEY_EQUIPMENT);
    const idx = all.findIndex((item) => item.id === id);
    if (idx === -1) return null;
    const updated: StargraveEquipment = { ...data, id, isCustom: true };
    const next = [...all];
    next[idx] = updated;
    writeAll(KEY_EQUIPMENT, next);
    return updated;
  },

  deleteEquipment(id: string): void {
    writeAll(KEY_EQUIPMENT, readAll<StargraveEquipment>(KEY_EQUIPMENT).filter((item) => item.id !== id));
  },

  getAllSoldiers(): StargraveSoldierProfile[] {
    return readAll<StargraveSoldierProfile>(KEY_SOLDIERS);
  },

  createSoldier(data: Omit<StargraveSoldierProfile, 'id' | 'isCustom'>): StargraveSoldierProfile {
    const profile: StargraveSoldierProfile = { ...data, id: crypto.randomUUID(), isCustom: true };
    writeAll(KEY_SOLDIERS, [...readAll<StargraveSoldierProfile>(KEY_SOLDIERS), profile]);
    return profile;
  },

  updateSoldier(id: string, data: Omit<StargraveSoldierProfile, 'id' | 'isCustom'>): StargraveSoldierProfile | null {
    const all = readAll<StargraveSoldierProfile>(KEY_SOLDIERS);
    const idx = all.findIndex((profile) => profile.id === id);
    if (idx === -1) return null;
    const updated: StargraveSoldierProfile = { ...data, id, isCustom: true };
    const next = [...all];
    next[idx] = updated;
    writeAll(KEY_SOLDIERS, next);
    return updated;
  },

  deleteSoldier(id: string): void {
    writeAll(KEY_SOLDIERS, readAll<StargraveSoldierProfile>(KEY_SOLDIERS).filter((profile) => profile.id !== id));
  },

  getAllBuffs(): StargraveBuff[] {
    return readAll<StargraveBuff>(KEY_BUFFS);
  },

  createBuff(data: Omit<StargraveBuff, 'id' | 'isCustom'>): StargraveBuff {
    const buff: StargraveBuff = { ...data, id: crypto.randomUUID(), isCustom: true };
    writeAll(KEY_BUFFS, [...readAll<StargraveBuff>(KEY_BUFFS), buff]);
    return buff;
  },

  updateBuff(id: string, data: Omit<StargraveBuff, 'id' | 'isCustom'>): StargraveBuff | null {
    const all = readAll<StargraveBuff>(KEY_BUFFS);
    const idx = all.findIndex((buff) => buff.id === id);
    if (idx === -1) return null;
    const updated: StargraveBuff = { ...data, id, isCustom: true };
    const next = [...all];
    next[idx] = updated;
    writeAll(KEY_BUFFS, next);
    return updated;
  },

  deleteBuff(id: string): void {
    writeAll(KEY_BUFFS, readAll<StargraveBuff>(KEY_BUFFS).filter((buff) => buff.id !== id));
  },
};
